import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Plus } from "lucide-react";
import PageWrapper from "../layout/PageWrapper";
import Button from "../forms/Button";
import { EmptyState, ErrorState, SkeletonRows } from "./shared";

/**
 * Shared layout for the Projects / Squads / Sprints list pages.
 * Renders a table: `columns` is [{ header, render(item), className? }] and each
 * row opens `rowLink(item)`. On narrow screens the table scrolls horizontally
 * inside its card, so the page itself never overflows.
 */
const CatalogListLayout = ({
  title,
  subtitle,
  icon,
  canCreate,
  createLabel,
  onCreate,
  loading,
  error,
  onRetry,
  items,
  emptyTitle,
  emptyMessage,
  columns,
  rowLink,
}) => {
  const navigate = useNavigate();
  return (
  <PageWrapper
    title={title}
    subtitle={subtitle}
    actions={
      canCreate && (
        <Button size="sm" onClick={onCreate}>
          <Plus className="w-4 h-4" /> {createLabel}
        </Button>
      )
    }
  >
    <div className="mb-4">
      <Link to="/task-board" className="inline-flex items-center gap-1.5 text-sm text-gray-600 dark:text-light-400 hover:text-primary-accent">
        <ArrowLeft className="w-4 h-4" /> Back to board
      </Link>
    </div>

    {error ? (
      <ErrorState message={error} onRetry={onRetry} />
    ) : loading ? (
      <SkeletonRows rows={4} />
    ) : items.length === 0 ? (
      <div className="bg-white dark:bg-primary-card rounded-xl border border-gray-200 dark:border-dark-100">
        <EmptyState icon={icon} title={emptyTitle} message={emptyMessage} />
      </div>
    ) : (
      <div className="bg-white dark:bg-primary-card rounded-xl border border-gray-200 dark:border-dark-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="table-header">
              <tr>
                {columns.map((col) => (
                  <th key={col.header} scope="col" className={`px-4 py-3 text-left whitespace-nowrap ${col.className || ""}`}>
                    {col.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-dark-100">
              {items.map((item) => (
                <tr
                  key={item._id}
                  className="table-row cursor-pointer"
                  onClick={() => navigate(rowLink(item))}
                >
                  {columns.map((col, i) => (
                    <td key={col.header} className={`px-4 py-3 align-middle text-gray-700 dark:text-light-300 ${col.className || ""}`}>
                      {i === 0 ? (
                        // Real link in the first cell keeps rows keyboard- and middle-click friendly.
                        <Link
                          to={rowLink(item)}
                          onClick={(e) => e.stopPropagation()}
                          className="font-medium text-gray-900 dark:text-light-100 hover:text-primary-accent"
                        >
                          {col.render(item)}
                        </Link>
                      ) : (
                        col.render(item)
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )}
  </PageWrapper>
  );
};

export default CatalogListLayout;
